import { useEffect, useState } from 'react';

import { type Skill } from '@atlassian/rovo-platform-types/skills';
import { appSwitcherBffAvailableProductsFetch } from '@atlassian/switcher/app-switcher-bff-available-products-provider';

import { useStableEventCallback } from '../../common/hooks/useStableEventCallback';

type EligibilityState = {
	cloudId: string;
	productTypes: ReadonlySet<string>;
	status: 'loading' | 'ready' | 'error';
};

type AiEligibilityState = {
	cloudId: string;
	isRovoLLMEnabled: boolean;
	status: 'loading' | 'ready' | 'error';
};

type SkillEligibilityOptions = {
	cloudId: string | null | undefined;
	enabled?: boolean;
};

const JIRA_PRODUCT_TYPES = new Set([
	'JIRA_SOFTWARE',
	'JIRA_BUSINESS',
	'JIRA_PRODUCT_DISCOVERY',
	'JIRA_SERVICE_DESK',
	'JIRA_CUSTOMER_SERVICE',
]);
const NO_PRODUCT_TYPES: ReadonlySet<string> = new Set();

const normalizeCloudId = (cloudId: string | null | undefined): string => cloudId?.trim() ?? '';

const hasJira = (productTypes: ReadonlySet<string>): boolean =>
	[...JIRA_PRODUCT_TYPES].some((productType) => productTypes.has(productType));

const isProductEligible = (
	skill: Skill,
	productTypes: ReadonlySet<string>,
	isRovoLLMEnabled: boolean,
): boolean => {
	switch (skill.slug) {
		case 'manage-capacity-plan':
		case 'demofactory-ops':
			return hasJira(productTypes);
		case 'generate-case-outcome-report':
		case 'suggest-incident-expert':
		case 'find-similar-incidents':
		case 'manage-workforce':
			return productTypes.has('JIRA_SERVICE_DESK');
		case 'build-skill':
			return isRovoLLMEnabled && productTypes.has('STUDIO');
		case 'request-review':
		case 'export-page-as-pdf':
		case 'get-approval-status':
		case 'submit-approval-decision':
			return productTypes.has('CONFLUENCE');
		default:
			return true;
	}
};

/**
 * Returns a synchronous skill predicate for the signed-in user's products on the current site.
 * Product access stays unresolved until the scoped App Switcher response has loaded.
 *
 * Discovery filters declared site-level entitlements. These additional checks enforce the
 * signed-in user's product access and Skill Builder's AI/Studio requirements.
 */
export const useSlashCommandSkillEligibility = ({
	cloudId,
	enabled = true,
}: SkillEligibilityOptions): ((skill: Skill) => boolean) => {
	const normalizedCloudId = normalizeCloudId(cloudId);
	const [state, setState] = useState<EligibilityState>({
		cloudId: '',
		productTypes: new Set(),
		status: 'loading',
	});
	const [aiState, setAiState] = useState<AiEligibilityState>({
		cloudId: '',
		isRovoLLMEnabled: false,
		status: 'loading',
	});

	useEffect(() => {
		if (!enabled || !normalizedCloudId) {
			setState({
				cloudId: normalizedCloudId,
				productTypes: new Set(),
				status: 'error',
			});
			return;
		}

		let cancelled = false;
		setState({
			cloudId: normalizedCloudId,
			productTypes: new Set(),
			status: 'loading',
		});

		void appSwitcherBffAvailableProductsFetch({ cloudId: normalizedCloudId })
			.then((response) => {
				if (cancelled) {
					return;
				}

				const site = response.sites.find(
					(candidate) => candidate.cloudId?.toLowerCase() === normalizedCloudId.toLowerCase(),
				);
				setState({
					cloudId: normalizedCloudId,
					productTypes: new Set(
						site?.availableProducts.map(({ productType }) => productType) ?? [],
					),
					status: site && !response.isPartial ? 'ready' : 'error',
				});
			})
			.catch(() => {
				if (!cancelled) {
					setState({
						cloudId: normalizedCloudId,
						productTypes: new Set(),
						status: 'error',
					});
				}
			});

		return () => {
			cancelled = true;
		};
	}, [enabled, normalizedCloudId]);

	useEffect(() => {
		if (!enabled || !normalizedCloudId) {
			setAiState({
				cloudId: normalizedCloudId,
				isRovoLLMEnabled: false,
				status: 'error',
			});
			return;
		}

		let cancelled = false;
		setAiState({
			cloudId: normalizedCloudId,
			isRovoLLMEnabled: false,
			status: 'loading',
		});

		// This site-and-user entitlement is independent of App Switcher's Rovo rollout state.
		void fetch('/gateway/api/assist/rovo/v2/entitlement', {
			headers: { 'X-Cloudid': normalizedCloudId },
		})
			.then(async (response) => {
				if (!response.ok) {
					throw new Error('Unable to resolve Rovo AI entitlement');
				}
				const entitlement: { isRovoLLMEnabled?: unknown } = await response.json();
				if (!cancelled) {
					setAiState({
						cloudId: normalizedCloudId,
						isRovoLLMEnabled: entitlement.isRovoLLMEnabled === true,
						status: 'ready',
					});
				}
			})
			.catch(() => {
				if (!cancelled) {
					setAiState({
						cloudId: normalizedCloudId,
						isRovoLLMEnabled: false,
						status: 'error',
					});
				}
			});

		return () => {
			cancelled = true;
		};
	}, [enabled, normalizedCloudId]);

	return useStableEventCallback((skill: Skill): boolean => {
		if (!enabled) {
			return true;
		}

		const isRovoLLMEnabled =
			aiState.cloudId.toLowerCase() === normalizedCloudId.toLowerCase() &&
			aiState.status === 'ready' &&
			aiState.isRovoLLMEnabled;
		if (
			!normalizedCloudId ||
			state.cloudId.toLowerCase() !== normalizedCloudId.toLowerCase() ||
			state.status !== 'ready'
		) {
			return isProductEligible(skill, NO_PRODUCT_TYPES, isRovoLLMEnabled);
		}

		return isProductEligible(skill, state.productTypes, isRovoLLMEnabled);
	});
};
